/**
 * Загрузчики «по ключу» для окон сущностей.
 *
 * Навигатор открывает окно только по ключу (код макета, артикул, id черновика,
 * nmID/offer_id). Окна ВБ, ОЗОН и Я.Маркета сами догружают полную карточку по
 * ключу, поэтому здесь — то, чего им не хватает: строка черновика ПИМ, карточка
 * сайта, название кабинета и справочник категорий товара.
 */

import {
  pimMarketplaceCardsAPI,
  type PimMarketplace,
  type PimMarketplaceCard,
} from '../../features/marketplace-catalog/pim-drafts-api'
import { siteAPI } from '../../features/site/api'
import type { SiteProductCard } from '../../features/site/types'
import { sellersAPI } from '../../features/sellers/api'
import { productsV2API } from '../../features/products-v2/api'

/** Строка черновика по id. Кабинет и площадка нужны серверу для отбора. */
export async function loadDraftCard(
  sellerId: number,
  marketplace: PimMarketplace,
  cardId: number,
  signal?: AbortSignal,
): Promise<PimMarketplaceCard | null> {
  const page = await pimMarketplaceCardsAPI.list(
    sellerId,
    { marketplace, id: cardId, limit: 1, offset: 0, facets: 0 },
    signal,
  )
  return page.items.find((card) => card.id === cardId) ?? page.items[0] ?? null
}

/** Карточка сайта по артикулу. */
export function loadSiteCard(article: string): Promise<SiteProductCard> {
  return siteAPI.getProductCard(article)
}

const sellerNames = new Map<string, string | null>()

/** Название кабинета для шапки окна; ошибка не критична — окно покажет «#id». */
export async function loadSellerName(sellerId: number | string): Promise<string | null> {
  const key = String(sellerId)
  if (sellerNames.has(key)) return sellerNames.get(key) ?? null
  try {
    const response = await sellersAPI.getSeller(Number(sellerId))
    const data = response.data?.data ?? response.data
    const seller = data?.seller ?? data
    const name = String(seller?.name || seller?.short_name || seller?.shortName || '').trim() || null
    sellerNames.set(key, name)
    return name
  } catch {
    return null
  }
}

let categoriesPromise: Promise<Array<{ id: number; description: string }>> | null = null

/** Справочник категорий товара для окна товара; грузится один раз за сессию. */
export function loadProductCategories(): Promise<Array<{ id: number; description: string }>> {
  if (!categoriesPromise) {
    categoriesPromise = productsV2API
      .getFilterOptions()
      .then((options) => options.categories || [])
      .catch(() => {
        categoriesPromise = null
        return []
      })
  }
  return categoriesPromise
}
